import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const CATEGORIE_COSTI = [["nolo", "Nolo"], ["smaltimento", "Smaltimento"], ["spese_varie", "Spese varie"]];
const LBL = Object.fromEntries(CATEGORIE_COSTI);
const fmt = (n) => (n || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const vuoto = () => ({ data: new Date().toISOString().slice(0, 10), categoria: "nolo", descrizione: "", importo: "", note: "" });

export const AltriCosti = ({ cantiereId, costi, onChanged }) => {
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const totale = costi.reduce((s, c) => s + (Number(c.importo) || 0), 0);

  const save = async () => {
    if (!form.data || !(Number(form.importo) >= 0) || form.importo === "") { toast.error("Inserisci data e importo"); return; }
    const body = { ...form, importo: Number(form.importo) };
    try {
      if (editId) await api.put(`/costi-cantiere/${editId}`, body);
      else await api.post(`/cantieri/${cantiereId}/costi`, body);
      toast.success(editId ? "Costo aggiornato" : "Costo aggiunto");
      setForm(null); setEditId(null); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Eliminare il costo "${c.descrizione || LBL[c.categoria]}"?`)) return;
    await api.delete(`/costi-cantiere/${c.id}`); toast.success("Costo eliminato"); onChanged();
  };

  return (
    <div className="space-y-4" data-testid="altri-costi">
      <div className="flex flex-wrap items-stretch gap-3">
        <Card className="card-elevated flex-1 min-w-[200px]"><CardContent className="p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Totale altri costi</div>
          <div className="text-2xl font-extrabold text-primary" data-testid="altri-costi-totale">{fmt(totale)}</div>
          <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-muted-foreground">
            {CATEGORIE_COSTI.map(([k, l]) => (
              <span key={k} data-testid={`altri-costi-cat-${k}`}>{l}: <b className="text-foreground">{fmt(costi.filter((c) => c.categoria === k).reduce((s, c) => s + (Number(c.importo) || 0), 0))}</b></span>
            ))}
          </div>
        </CardContent></Card>
        <Button className="h-11 self-center" onClick={() => { setEditId(null); setForm(vuoto()); }} data-testid="altri-costi-add"><Plus className="h-4 w-4 mr-2" /> Aggiungi costo</Button>
      </div>
      <Card className="card-elevated"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary"><tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
            <th className="px-4 py-3">Data</th><th className="px-4 py-3">Categoria</th><th className="px-4 py-3">Descrizione</th>
            <th className="px-4 py-3 text-right">Importo</th><th className="px-4 py-3" />
          </tr></thead>
          <tbody>
            {costi.map((c) => (
              <tr key={c.id} className="border-t border-border" data-testid={`altri-costi-row-${c.id}`}>
                <td className="px-4 py-3 tabular-nums">{c.data}</td>
                <td className="px-4 py-3">{LBL[c.categoria] || c.categoria}</td>
                <td className="px-4 py-3">{c.descrizione || "—"}{c.note && <div className="text-xs text-muted-foreground">{c.note}</div>}</td>
                <td className="px-4 py-3 text-right font-semibold tabular-nums">{fmt(c.importo)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" onClick={() => { setEditId(c.id); setForm({ ...vuoto(), ...c, importo: String(c.importo) }); }} data-testid={`altri-costi-edit-${c.id}`}><Pencil className="h-3 w-3" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(c)} data-testid={`altri-costi-delete-${c.id}`}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                </td>
              </tr>
            ))}
            {costi.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">Nessun costo aggiuntivo registrato.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-md" data-testid="altri-costi-dialog">
          <DialogHeader><DialogTitle>{editId ? "Modifica costo" : "Nuovo costo"}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data</Label><Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="costo-data" /></div>
              <div><Label>Categoria</Label>
                <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                  <SelectTrigger data-testid="costo-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIE_COSTI.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Descrizione</Label><Input value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} placeholder="Es. Nolo gru 2 giorni" data-testid="costo-descrizione" /></div>
              <div className="col-span-2"><Label>Importo €</Label><Input type="number" step="0.01" min="0" value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })} data-testid="costo-importo" /></div>
              <div className="col-span-2"><Label>Note</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="costo-note" /></div>
              <Button className="col-span-2 h-11" onClick={save} data-testid="costo-save">Salva</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
